import { useMediaQuery } from '@mantine/hooks';
import type { EasingDefinition } from 'framer-motion';

const LONG_EASE = {
	duration: 10,
	ease: 'easeInOut' as EasingDefinition
};

const CENTER_BASE = {
	top: '50%',
	left: '50%',
	x: '-50%',
	y: '-50%',
	opacity: 1
};

const CORNER_BASE = {
	top: '0%',
	left: '0%',
	x: '0%',
	y: '0%',
	opacity: 1
};

const repeat = (value: string, count: number) => new Array(count).fill(value);

const getDeviceConfig = (isMobile: boolean) => ({
	centerFont: isMobile ? 26 : 48,
	cornerFont: isMobile ? 22 : 26,
	centerLeftFont: 32,

	centerToCorner: {
		left: isMobile ? '50%' : '40%',
		end: isMobile ? '50%' : '0%',
		xEnd: isMobile ? '-50%' : '0%',
		scale: isMobile
			? [1, 0.9, 1.1, 0.9, 1.05, 1, 1, 1]
			: [1.3, 0.9, 1.3, 0.9, 1.3, 1, 1, 1],
		fontSize: isMobile
			? [32, 26, 32, 26, 32, 26, 24, 22]
			: [48, 32, 48, 32, 48, 32, 28, 26]
	},

	cornerToCenter: {
		top: isMobile
			? ['0%', '20%', '40%', '50%', '50%']
			: ['0%', '0%', '50%', '50%', '50%'],
		left: isMobile
			? ['0%', '50%', '50%', '50%', '50%']
			: ['0%', '0%', '7%', '7%', '7%'],
		x: isMobile
			? ['0%', '-50%', '-50%', '-50%', '-50%']
			: ['0%', '0%', '-40%', '-50%', '-50%'],
		y: isMobile
			? ['0%', '-50%', '-50%', '-50%', '-50%']
			: ['0%', '0%', '-40%', '-50%', '-50%'],
		fontSize: isMobile ? [20, 26, 32, 40, 42] : [26, 28, 32, 40, 42],
		times: isMobile ? [0, 0.2, 0.5, 0.8, 1] : [0, 0.15, 0.4, 0.8, 1]
	}
});

export const createTrainingAnimationVariants = () => {
	const isMobile = useMediaQuery('(max-width: 768px)');
	const cfg = getDeviceConfig(isMobile);

	return {
		centerIdle: {
			...CENTER_BASE,
			position: 'absolute',
			fontSize: cfg.centerFont
		},

		centerToCorner: {
			...CENTER_BASE,
			width: isMobile ? '100%' : undefined,

			top: repeat('50%', 6).concat(cfg.centerToCorner.end),
			left: repeat(cfg.centerToCorner.left, 6).concat(cfg.centerToCorner.end),
			x: repeat('-50%', 6).concat(cfg.centerToCorner.xEnd),
			y: repeat('-50%', 6).concat(cfg.centerToCorner.xEnd),

			scale: cfg.centerToCorner.scale,
			fontSize: cfg.centerToCorner.fontSize,

			transition: {
				...LONG_EASE,
				times: [0, 0.1, 0.2, 0.3, 0.4, 0.8, 0.85, 1]
			}
		},

		cornerIdle: {
			...CORNER_BASE,
			position: isMobile ? 'static' : undefined,
			fontSize: cfg.cornerFont
		},

		cornerToCenter: {
			...CORNER_BASE,
			width: isMobile ? '100%' : undefined,

			top: cfg.cornerToCenter.top,
			left: cfg.cornerToCenter.left,
			x: cfg.cornerToCenter.x,
			y: cfg.cornerToCenter.y,
			fontSize: cfg.cornerToCenter.fontSize,

			transition: {
				...LONG_EASE,
				times: cfg.cornerToCenter.times
			}
		},

		centerLeftIdle: {
			position: isMobile ? 'static' : undefined,
			top: isMobile ? '0' : '50%',
			left: isMobile ? '0' : '6%',
			x: isMobile ? '0' : '25%',
			y: isMobile ? '0' : '-50%',
			fontSize: cfg.centerLeftFont,
			opacity: 1,
			transition: { duration: 0.4, ease: 'easeOut' as EasingDefinition }
		}
	};
};
